/**
 * Connector SIMPLIFICADO e OTIMIZADO
 *
 * ✅ NOVA ESTRATÉGIA SIMPLES:
 * 1. VALOR EXATO: Se só existe um produto com esse valor, conecta direto
 * 2. CÓDIGO FORNECEDOR: codProdForn do XML deve bater com pedido
 * 3. DESCRIÇÃO: Descrição do XML deve conter ou ser contida na do pedido
 *
 * ❌ REMOVIDO: Complexidade desnecessária, validações cruzadas, tolerâncias
 */

import { Produto, OrigemProduto } from "@PN/inclusao";

export interface ConnectorResult {
  conexoesAutomaticas: Produto[];
  produtosNaoConectados: Produto[];
  stats: {
    totalXml: number;
    totalPedido: number;
    conectados: number;
    saldo: number;
  };
}

export interface ConnectorOptions {
  xmlItems: Produto[];
  pedidoItems: Produto[];
  tabelaAtual?: Produto[];
  tipoNota?: string;
  onAddMesclado?: (item: Produto) => void;
  onRemoveXml?: (id: string) => void;
  onUpdateProdutoProtheus?: (items: Produto[]) => void;
  onClearProdutoProtheus?: () => void;
  onUpdateTabela?: (items: Produto[]) => void;
}

// =================================================================
// SERVIÇOS SIMPLIFICADOS
// =================================================================

/**
 * Serviço para gerenciar produtos conectados
 */
class ConnectedProductsManager {
  private connectedProducts = new Set<string>();

  addConnectedProduct(produto: Produto): void {
    const key = this.generateKey(produto);
    this.connectedProducts.add(key);
  }

  isProductConnected(produto: Produto): boolean {
    const key = this.generateKey(produto);
    return this.connectedProducts.has(key);
  }

  getConnectedCount(): number {
    return this.connectedProducts.size;
  }

  private generateKey(produto: Produto): string {
    return `${produto.pedidoCompra}_${produto.itemPedido}_${
      produto.codProdForn || produto.codProduto
    }_${produto.quantidade}`;
  }
}

/**
 * Serviço de busca SIMPLIFICADO e OTIMIZADO
 *
 * ✅ PRIORIDADE 1: VALOR EXATO (se único)
 * ✅ PRIORIDADE 2: CÓDIGO FORNECEDOR
 * ✅ PRIORIDADE 3: DESCRIÇÃO DO PRODUTO
 */
class ProductSearchService {
  private produtosPorCodForn: Map<string, Produto[]>;
  private produtosPorValor: Map<number, Produto[]>;

  constructor(pedidoItems: Produto[]) {
    this.produtosPorCodForn = this.buildCodFornMap(pedidoItems);
    this.produtosPorValor = this.buildValorMap(pedidoItems);
  }

  findProductByPriority(
    xmlItem: Produto,
    connectedManager: ConnectedProductsManager
  ): { produto: Produto | null; metodo: string } {
    // ✅ PRIORIDADE 1: VALOR EXATO (se único produto com esse valor)
    if (xmlItem.valorTotal && xmlItem.valorTotal > 0) {
      const valorExato = this.findByExactValueUnique(
        xmlItem.valorTotal,
        connectedManager
      );
      if (valorExato) {
        return { produto: valorExato, metodo: "valor_exato_unico" };
      }
    }

    // ✅ PRIORIDADE 2: CÓDIGO DO FORNECEDOR
    if (xmlItem.codProdForn) {
      const codigoMatch = this.findByCodForn(
        xmlItem.codProdForn,
        connectedManager
      );
      if (codigoMatch) {
        return { produto: codigoMatch, metodo: "codigo_fornecedor" };
      }
    }

    // ✅ PRIORIDADE 3: DESCRIÇÃO DO PRODUTO
    if (xmlItem.descProdForn) {
      const descricaoMatch = this.findByDescription(
        xmlItem.descProdForn,
        connectedManager
      );
      if (descricaoMatch) {
        return { produto: descricaoMatch, metodo: "descricao_produto" };
      }
    }

    return { produto: null, metodo: "" };
  }

  // ✅ PRIORIDADE 1: Valor exato (se só existe um produto com esse valor)
  private findByExactValueUnique(
    valor: number,
    connectedManager: ConnectedProductsManager
  ): Produto | null {
    const produtosComValor =
      this.produtosPorValor.get(Math.round(valor * 100)) || [];
    const disponiveis = produtosComValor.filter(
      (p) => !connectedManager.isProductConnected(p)
    );

    // Só conecta se for ÚNICO produto com esse valor
    if (disponiveis.length === 1) {
      return disponiveis[0];
    }

    return null;
  }

  // ✅ PRIORIDADE 2: Código do fornecedor
  private findByCodForn(
    codForn: string,
    connectedManager: ConnectedProductsManager
  ): Produto | null {
    const produtos = this.produtosPorCodForn.get(codForn.trim()) || [];
    const disponivel = produtos.find(
      (p) => !connectedManager.isProductConnected(p)
    );
    return disponivel || null;
  }

  // ✅ PRIORIDADE 3: Descrição do produto (contém ou é contida)
  private findByDescription(
    descXml: string,
    connectedManager: ConnectedProductsManager
  ): Produto | null {
    const descXmlLower = descXml.toLowerCase().trim();

    // Busca em todos os produtos disponíveis
    for (const produtos of this.produtosPorCodForn.values()) {
      for (const produto of produtos) {
        if (connectedManager.isProductConnected(produto)) continue;
        if (!produto.descricao) continue;

        const descPedidoLower = produto.descricao.toLowerCase().trim();

        // Verifica se uma descrição contém a outra
        if (
          descXmlLower.includes(descPedidoLower) ||
          descPedidoLower.includes(descXmlLower)
        ) {
          return produto;
        }
      }
    }

    return null;
  }

  // Métodos auxiliares para construir mapas
  private buildCodFornMap(pedidoItems: Produto[]): Map<string, Produto[]> {
    const map = new Map<string, Produto[]>();
    pedidoItems.forEach((produto) => {
      if (produto.codProdForn) {
        const key = produto.codProdForn.trim();
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(produto);
      }
    });
    return map;
  }

  private buildValorMap(pedidoItems: Produto[]): Map<number, Produto[]> {
    const map = new Map<number, Produto[]>();
    pedidoItems.forEach((produto) => {
      if (produto.valorTotal && produto.valorTotal > 0) {
        // Arredonda para 2 casas decimais para agrupar valores similares
        const valorChave = Math.round(produto.valorTotal * 100);
        if (!map.has(valorChave)) map.set(valorChave, []);
        map.get(valorChave)!.push(produto);
      }
    });
    return map;
  }
}

/**
 * Serviço para criar produtos mesclados
 */
class ProductMergerService {
  static createMergedProduct(xmlItem: Produto, pedidoItem: Produto): Produto {
    return {
      // Dados do XML (estrutura original)
      codProdForn: xmlItem.codProdForn,
      descProdForn: xmlItem.descProdForn,
      quantidade: pedidoItem.quantidade, // ✅ CORREÇÃO: Usa quantidade do PEDIDO, não da XML
      valorTotal: xmlItem.valorTotal,
      origemProdForn: xmlItem.origemProdForn,
      ncmProdForn: xmlItem.ncmProdForn,
      umFornecedor: xmlItem.umFornecedor,
      itemNF: xmlItem.itemNF,

      // Dados do pedido (adicionados quando conectado)
      codProduto: pedidoItem.codProduto,
      descricao: pedidoItem.descricao,
      origemProduto: pedidoItem.origemProduto,
      ncmProduto: pedidoItem.ncmProduto,
      unidadeMedida: pedidoItem.unidadeMedida,
      grupoTributario: pedidoItem.grupoTributario,
      pedidoCompra: pedidoItem.pedidoCompra,
      itemPedido: pedidoItem.itemPedido,

      // ✅ CAMPOS OBRIGATÓRIOS DO PROTHEUS
      centroCusto: pedidoItem.centroCusto || "",
      armazemPadrao: pedidoItem.armazemPadrao || "",
      grupoProduto: pedidoItem.grupoProduto || "",
      tipoProduto: pedidoItem.tipoProduto || "",

      // Campos de conversão (simplificados)
      tpFato: "M" as const,
      fatorConversao: 1,

      // Preserva quantidade original do XML
      qtdeFornecedor: xmlItem.quantidade, // ✅ Quantidade da XML fica aqui

      // Metadados
      origem: xmlItem.origem,
      idUnico: xmlItem.idUnico,
    };
  }
}

// =================================================================
// FUNÇÃO PRINCIPAL SIMPLIFICADA
// =================================================================

export function connectXmlWithPedido(
  options: ConnectorOptions
): ConnectorResult {
  const { xmlItems, pedidoItems, tabelaAtual, tipoNota, onUpdateTabela } =
    options;

  // Inicializa serviços
  const connectedManager = new ConnectedProductsManager();
  const searchService = new ProductSearchService(pedidoItems);

  const conexoesAutomaticas: Produto[] = [];

  // Processa itens XML
  if (tabelaAtual && tabelaAtual.length > 0) {
    const substituicoes = new Map<number, Produto>();

    tabelaAtual.forEach((item, index) => {
      if (item.origem === OrigemProduto.XML && item.codProdForn) {
        const resultado = searchService.findProductByPriority(
          item,
          connectedManager
        );

        if (resultado.produto) {
          // ✅ VALIDAÇÃO DE DISCREPÂNCIA
          const quantidadeXml = item.quantidade || 0;
          const quantidadePedido = resultado.produto.quantidade || 0;
          const diferencaPercentual = quantidadePedido > 0 
            ? Math.abs((quantidadeXml - quantidadePedido) / quantidadePedido) * 100 
            : 0;

          // ✅ BLOQUEIA CONEXÃO SE DISCREPÂNCIA > 50%
          if (diferencaPercentual > 50) {
            console.warn(`⚠️ [connectXmlWithPedido] CONEXÃO BLOQUEADA - Discrepância muito alta:`, {
              produto: resultado.produto.codProduto,
              quantidadePedido,
              quantidadeXml,
              diferencaPercentual: diferencaPercentual.toFixed(2) + '%',
              motivo: 'Discrepância de quantidade muito alta (>50%)'
            });
            // Não conecta este produto - pula para o próximo
          } else {
            // Adiciona produto conectado
            connectedManager.addConnectedProduct(resultado.produto);

            // Cria produto mesclado
            const produtoMesclado = ProductMergerService.createMergedProduct(
              item,
              resultado.produto
            );

            substituicoes.set(index, produtoMesclado);
            conexoesAutomaticas.push(produtoMesclado);
          }
        }
      }
    });

    // Aplica substituições na tabela
    if (substituicoes.size > 0) {
      const novaTabela = tabelaAtual.map(
        (item, index) => substituicoes.get(index) || item
      );

      if (onUpdateTabela) {
        onUpdateTabela(novaTabela);
      }
    }
  }

  // Calcula produtos não conectados
  const produtosNaoConectados = pedidoItems.filter(
    (produto) => !connectedManager.isProductConnected(produto)
  );

  const stats = {
    totalXml: xmlItems.length,
    totalPedido: pedidoItems.length,
    conectados: conexoesAutomaticas.length,
    saldo: produtosNaoConectados.length,
  };

  return {
    conexoesAutomaticas,
    produtosNaoConectados,
    stats,
  };
}
